import React, { useEffect, useRef, useState } from 'react';
import { TimepickerUI } from 'timepicker-ui';
import moment from 'moment-timezone';

const getTimepickerUITimeStr = time => moment(time).format('hh:mm A');

const getTimepickerUITime = e => {
    let hours = parseInt(e.detail.hour);
    if (e.detail.type === 'AM' && hours === 12) {
        hours = 0;
    } else if (e.detail.type === 'PM' && hours !== 12) {
        hours += 12;
    }
    const minutes = parseInt(e.detail.minutes);
    return new Date(new Date().setHours(hours, minutes, 0, 0));
};

const TimepickerUIHoursInput = ({
    names,
    values,
    disabled = { date: false, startTime: false, endTime: false },
    index,
    isNew = true,
    shouldOpenStartTime = false,
    onStartOpened,
    onTimeSelected,
}) => {
    const [isStartTime, setIsStartTime] = useState(false);
    const [isEndTime, setIsEndTime] = useState(false);

    const showStartTimeUnset = !isStartTime && isNew;
    const showEndTimeUnset = !isEndTime && isNew;

    const [startDisplayValue, setStartDisplayValue] = useState(getTimepickerUITimeStr(values.startTime));
    const [endDisplayValue, setEndDisplayValue] = useState(getTimepickerUITimeStr(values.endTime));

    const startTimeRef = useRef(null);
    const endTimeRef = useRef(null);
    const startPickerRef = useRef(null);
    const valuesRef = useRef(values);

    // Keep valuesRef current on every render so revert callbacks see the latest Formik values
    valuesRef.current = values;

    const startHandlerRef = useRef(null);
    startHandlerRef.current = e => {
        const startTime = getTimepickerUITime(e);
        setIsStartTime(true);
        setIsEndTime(true);

        const endTime = new Date(startTime.getTime() + 60 * 60000);
        const endTimeFormatted = getTimepickerUITimeStr(endTime);

        setStartDisplayValue(getTimepickerUITimeStr(startTime));
        setEndDisplayValue(endTimeFormatted);

        const revertStart = () => {
            setStartDisplayValue(getTimepickerUITimeStr(valuesRef.current.startTime));
            setEndDisplayValue(getTimepickerUITimeStr(valuesRef.current.endTime));
        };
        onTimeSelected(
            startTime,
            names.startTime,
            'start time',
            [{ fieldName: names.endTime, time: endTime }],
            revertStart,
        );
    };

    const endHandlerRef = useRef(null);
    endHandlerRef.current = e => {
        const time = getTimepickerUITime(e);
        setIsEndTime(true);
        setEndDisplayValue(getTimepickerUITimeStr(time));

        const revertEnd = () => {
            setEndDisplayValue(getTimepickerUITimeStr(valuesRef.current.endTime));
        };
        onTimeSelected(time, names.endTime, 'stop time', [], revertEnd);
    };

    const tmOptions = {
        mobile: false,
        animation: false,
        okLabel: 'Confirm',
    };

    // start time — runs once on mount; wrapper calls latest handler via ref
    useEffect(() => {
        const tmStart = startTimeRef.current;
        const tmStartPicker = new TimepickerUI(tmStart, {
            ...tmOptions,
            timeLabel: 'Start time',
        });
        tmStartPicker.create();
        startPickerRef.current = tmStartPicker;
        const handler = e => startHandlerRef.current(e);
        tmStart.addEventListener('accept', handler);
        return () => tmStart.removeEventListener('accept', handler);
    }, []);

    // end time — runs once on mount; wrapper calls latest handler via ref
    useEffect(() => {
        const tmEnd = endTimeRef.current;
        const tmEndPicker = new TimepickerUI(tmEnd, {
            ...tmOptions,
            timeLabel: 'End time',
        });
        tmEndPicker.create();
        const handler = e => endHandlerRef.current(e);
        tmEnd.addEventListener('accept', handler);
        return () => tmEnd.removeEventListener('accept', handler);
    }, []);

    // auto-open start time when date is selected
    useEffect(() => {
        if (shouldOpenStartTime && startPickerRef.current) {
            startPickerRef.current.open();
            onStartOpened?.();
        }
    }, [shouldOpenStartTime]);

    return (
        <>
            <div className="time-container-row spacer">
                <span className="placeholder">{'Start *'}</span>
                <div className="timepicker-ui" ref={startTimeRef}>
                    <input
                        type="text"
                        className={`timepicker-ui-input ${showStartTimeUnset ? ' field-unset' : ''}`}
                        value={startDisplayValue}
                        readOnly
                    />
                </div>
            </div>
            <div className="time-container-row spacer">
                <span className="placeholder">{'Stop *'}</span>
                <div className="timepicker-ui" ref={endTimeRef}>
                    <input
                        type="text"
                        className={`timepicker-ui-input ${showEndTimeUnset ? ' field-unset' : ''}`}
                        value={endDisplayValue}
                        readOnly
                    />
                </div>
            </div>
        </>
    );
};

export default TimepickerUIHoursInput;
